import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import ReactPlayer from 'react-player';
import { appActions } from '../../store/modules/app';
import useImageUpload from '../../hooks/common/useImageUpload';
import Img from '../Img';
import Anchor from '../link/Anchor';
import Modal from '../Modal';
import Badge from '../Badge';
import DragSortListWrap from '../DragSortListWrap';
import ReactCropper from '../ReactCropper';
import { reOrderArray } from '../../lib/common';
import { CloseOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { AiOutlineFileImage, AiOutlineVideoCamera } from 'react-icons/ai';
import { ViewModalImageType } from '../../types/common/ImageDataType';
import { PRODUCT_ATTACHMENT_TYPE } from '../../constants/service/app/productConstants';
import { Attachment } from '../input/ImageAndVideoUploadV2/types';
import './ImgUpload.scss';

interface NewImgUploadProps {
  maxCnt: number;
  modalTitle: string;
  isEditable?: boolean;
  canAddVideo?: boolean;
  onlyVideo?: boolean;
  name: string;
  onChange?: (imageUrls: string[]) => void;
  value: string[];
}

const NewImgUpload = (props: NewImgUploadProps): JSX.Element => {
  const { maxCnt, modalTitle, isEditable = true, canAddVideo = false, onlyVideo = false, onChange, value } = props;

  const {
    imageList,
    setImageList,
    isModalOpen,
    cropModalToggle,
    uploadCroppedFile,
    files,
    handleUploadFile,
    handleDeleteImage,
    indexToUpdate,
  } = useImageUpload({ onChange, value });

  const dispatch = useDispatch();
  const { appToggleModal, setData } = appActions;

  const cropperRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };
  /**
   * 이미지 순서 변경하기
   */
  const onDragEnd = (result: any) => {
    if (!isEditable) return;

    if (!result.destination) {
      return;
    }
    const newList = reOrderArray(imageList, result.source.index, result.destination.index);
    setImageList(newList);
    if (onChange) {
      onChange(newList);
    }
  };
  /**
   * 이미지 미리보기
   */
  const openImgViewModal = (data: ViewModalImageType) => {
    const targetModalData = {
      name: 'imgViewModal',
      data: {
        url: data.url,
      },
    };
    if (data.type === PRODUCT_ATTACHMENT_TYPE.VIDEO) {
      targetModalData.name = 'videoViewModal';
    }
    dispatch(appToggleModal(targetModalData));
  };

  const addVideo = () => {
    const targetModalData = {
      name: 'videoUploadModal',
      data: {
        // todo: 영상 업르드를 사용하는 컴포넌트에서 해당 타입 지정하기
        addVideo: (value: any) =>
          dispatch(
            setData({
              value,
            }),
          ),
      },
    };
    dispatch(appToggleModal(targetModalData));
  };

  useEffect(() => {
    if (!value) return;
    setImageList(value);
  }, [value]);

  return (
    <div className="img-upload">
      {/* 크롭 모달 */}
      <Modal
        title={modalTitle}
        onClose={cropModalToggle}
        isOpen={isModalOpen}
        width={650}
        height={520}
        maskClosable={false}
        isFooter={true}
        okText="확인"
        cancelText="취소"
        onOk={() => cropperRef.current.handleClickCrop()}
      >
        <ReactCropper
          rotatable={true}
          uploadCroppedFile={uploadCroppedFile}
          ref={cropperRef}
          files={files}
          openImgViewModal={openImgViewModal}
        />
      </Modal>
      <div className="img-container">
        <div className="wrap-img">
          <DragSortListWrap
            onDragEnd={onDragEnd}
            list={imageList as Attachment[]}
            item={(data: any, index: number) => {
              return (
                <div className="img">
                  {data.type === PRODUCT_ATTACHMENT_TYPE.VIDEO ? (
                    <ReactPlayer className="react-player" width={140} height={140} light={true} url={data} />
                  ) : (
                    <Img clickAble={false} effect src={data} />
                  )}
                  {imageList?.length > 1 ? (
                    <p className="orderNumber">
                      {/* 번호 */}
                      <Badge className="badge" color="primary" badgeContent={index + 1} />
                    </p>
                  ) : null}
                  <p className="toolbar">
                    {/* 미리보기 */}
                    <Anchor
                      onClick={() =>
                        openImgViewModal({
                          url: data,
                        })
                      }
                      text={<SearchOutlined rev={undefined} />}
                    />
                    {isEditable && (
                      <>
                        {/*변경하기*/}
                        <Anchor
                          onClick={() => {
                            indexToUpdate.current = index;
                            handleClick();
                          }}
                          text={<EditOutlined rev={undefined} />}
                        />
                        {/* 삭제하기 */}
                        <Anchor onClick={() => handleDeleteImage(index)} text={<CloseOutlined rev={undefined} />} />
                      </>
                    )}
                  </p>
                </div>
              );
            }}
          />
          {/*이미지 추가*/}
          {!isModalOpen && isEditable && (
            <div
              className={cx('register-img-wrap', {
                hide: imageList.length >= maxCnt,
              })}
            >
              <div className="register-img">
                <a className="add-btn">
                  {canAddVideo || onlyVideo ? (
                    <>
                      {!onlyVideo && (
                        <div className="add-img-btn">
                          <AiOutlineFileImage />
                        </div>
                      )}
                      <div
                        className={cx('add-video-btn', {
                          'only-video': onlyVideo,
                        })}
                        onClick={() => addVideo()}
                      >
                        <AiOutlineVideoCamera />
                      </div>
                    </>
                  ) : (
                    <PlusOutlined onClick={() => handleClick()} rev={undefined} />
                  )}
                </a>
                <input
                  ref={inputRef}
                  className={cx('file-input', {
                    'can-add-video': canAddVideo,
                  })}
                  accept="image/*"
                  type="file"
                  id="fileUpload"
                  onChange={handleUploadFile}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewImgUpload;
